import type { ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'

interface SubtleTabsProps {
  value: string
  onValueChange: (value: string) => void
  children: ReactNode
  className?: string
}

interface SubtleTabsListProps {
  children: ReactNode
  className?: string
}

interface SubtleTabsTriggerProps {
  value: string
  children: ReactNode
  className?: string
}

interface SubtleTabsContentProps {
  value: string
  children: ReactNode
  className?: string
}

interface SubtleTabsContextType {
  value: string
  onValueChange: (value: string) => void
}

import { createContext, useContext } from 'react'

const SubtleTabsContext = createContext<SubtleTabsContextType | undefined>(
  undefined
)

function useSubtleTabs() {
  const context = useContext(SubtleTabsContext)
  if (!context) {
    throw new Error('useSubtleTabs must be used within SubtleTabs')
  }
  return context
}

export function SubtleTabs({
  value,
  onValueChange,
  children,
  className,
}: SubtleTabsProps) {
  return (
    <SubtleTabsContext.Provider value={{ value, onValueChange }}>
      <div className={cn('w-full', className)}>{children}</div>
    </SubtleTabsContext.Provider>
  )
}

export function SubtleTabsList({ children, className }: SubtleTabsListProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-start gap-0 border-b border-slate-200 dark:border-slate-700 bg-transparent',
        className
      )}
    >
      {children}
    </div>
  )
}

export function SubtleTabsTrigger({
  value,
  children,
  className,
}: SubtleTabsTriggerProps) {
  const { value: currentValue, onValueChange } = useSubtleTabs()
  const isActive = currentValue === value

  return (
    <button
      type='button'
      onClick={() => onValueChange(value)}
      className={cn(
        'relative px-4 py-3 text-sm font-medium transition-colors duration-200',
        'border-b-2 border-transparent',
        'hover:text-slate-700 dark:hover:text-slate-200',
        'focus:outline-none focus:text-slate-700 dark:focus:text-slate-200',
        isActive
          ? 'text-slate-900 dark:text-slate-100 border-slate-900 dark:border-slate-100'
          : 'text-slate-500 dark:text-slate-400',
        className
      )}
    >
      {children}
    </button>
  )
}

export function SubtleTabsContent({
  value,
  children,
  className,
}: SubtleTabsContentProps) {
  const { value: currentValue } = useSubtleTabs()

  if (currentValue !== value) {
    return null
  }

  return (
    <div className={cn('mt-6 focus:outline-none', className)}>{children}</div>
  )
}
